import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api.js';

// Hybrid vendor field. Keeps the free-text `vendor` string AND can optionally
// link to a structured Vendor record (vendorId). The dropdown merges the Vendor
// directory (preferred, with a "directory" badge) with the distinct free-text
// values from existing POs (the historical autocomplete). Picking a directory
// entry links (sets vendorId); typing/editing free text clears the link.
//
// onChange(vendor: string, vendorId: string | null)
export default function VendorPicker({ value, vendorId, onChange, required, placeholder }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [records, setRecords] = useState([]); // structured Vendor rows
  const [history, setHistory] = useState([]); // distinct free-text strings
  const containerRef = useRef(null);

  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      const qs = value ? `?q=${encodeURIComponent(value)}` : '';
      try {
        const [v, h] = await Promise.all([
          api.get(`/api/vendors${qs}`),
          api.get(`/api/autocomplete/vendors${qs}`),
        ]);
        if (active) {
          setRecords(v.data.vendors || []);
          setHistory(h.data.values || []);
        }
      } catch { /* ignore — picker still usable as plain free text */ }
    }, 150);
    return () => { active = false; clearTimeout(timer); };
  }, [value]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Hide history entries that duplicate a directory record's name (case-insensitive).
  const recordNames = new Set(records.map((r) => r.name.toLowerCase()));
  const freeText = history.filter((h) => !recordNames.has(h.toLowerCase()));

  const pickRecord = (r) => { onChange(r.name, r.id); setOpen(false); };
  const pickText = (s) => { onChange(s, null); setOpen(false); };
  const unlink = () => onChange(value, null);

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={value || ''}
          onChange={(e) => { onChange(e.target.value, null); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          className={`input ${vendorId ? 'pr-8 ring-1 ring-cyan-700/60' : ''}`}
        />
        {vendorId && (
          <button
            type="button"
            onClick={unlink}
            title={`${t('vendorPicker.linked')} — ${t('vendorPicker.unlink')}`}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-cyan-400 hover:text-cyan-200"
          >
            <LinkIcon />
          </button>
        )}
      </div>
      {open && (records.length > 0 || freeText.length > 0) && (
        <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md border border-zinc-800 bg-zinc-900 shadow-lg">
          {records.map((r) => (
            <li
              key={r.id}
              onClick={() => pickRecord(r)}
              className="flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 text-sm hover:bg-zinc-800"
            >
              <span className="flex items-center gap-1.5 truncate">
                <LinkIcon className="shrink-0 text-cyan-400" />
                <span className="truncate">{r.name}</span>
              </span>
              <span className="shrink-0 text-[10px] uppercase tracking-wider text-cyan-500/80">
                {t('vendorPicker.directory')}
              </span>
            </li>
          ))}
          {records.length > 0 && freeText.length > 0 && (
            <li className="border-t border-zinc-800" aria-hidden="true" />
          )}
          {freeText.map((s) => (
            <li
              key={s}
              onClick={() => pickText(s)}
              className="cursor-pointer truncate px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-800"
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LinkIcon({ className = '' }) {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}
